/**
 * Per-user Feishu OAuth client (personal-materials.md P4b). Status / start /
 * disconnect / send-confirm. `start` returns the authorize URL for an
 * AUTHENTICATED fetch (bearer token lives in the SPA, not a cookie), then the
 * caller navigates the top-level window to it. Routes 404 when the feature is
 * off or Feishu credentials are absent.
 */
import { authHeaders } from "./auth";

export interface FeishuUserConnection {
  connected: boolean;
  displayName?: string;
  openId?: string;
  tokenMask?: string;
  scopes?: string;
  sendConfirm?: boolean;
  connectedAt?: number;
}

export const feishuOAuthApi = {
  /** Current connection status. Returns disconnected on any failure. */
  status: async (): Promise<FeishuUserConnection> => {
    try {
      const res = await fetch("/api/feishu/oauth/status", { headers: authHeaders() });
      if (!res.ok) return { connected: false };
      return ((await res.json()) as { connection: FeishuUserConnection }).connection;
    } catch {
      return { connected: false };
    }
  },

  /** Fetch the authorize URL (authenticated), then navigate the window to Feishu. */
  begin: async (): Promise<void> => {
    const res = await fetch("/api/feishu/oauth/start", { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const { authorizeUrl } = (await res.json()) as { authorizeUrl: string };
    window.location.href = authorizeUrl;
  },

  /** Toggle confirm-before-send. */
  setSendConfirm: async (value: boolean): Promise<FeishuUserConnection> => {
    const res = await fetch("/api/feishu/oauth/send-confirm", {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ sendConfirm: value }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return ((await res.json()) as { connection: FeishuUserConnection }).connection;
  },

  /** Disconnect (revoke the local token + audit). */
  disconnect: async (): Promise<boolean> => {
    const res = await fetch("/api/feishu/oauth", { method: "DELETE", headers: authHeaders() });
    if (!res.ok) return false;
    return ((await res.json()) as { ok?: boolean }).ok ?? false;
  },
};
